import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, MessageCircle, Trash2, MoreVertical, CheckSquare, Square, Edit2, Users, Search, X, Download, BarChart3, ChevronLeft, ChevronDown } from 'lucide-react';
import apiService from '../services/apiService';
import ConversationDataCard from './ConversationDataCard';
import { useDraftedConversationIds } from '../hooks/useConversationDraft';
import { useRespondedConversationIds } from '../hooks/useConversationResponses';
import {
    synthesizeConversationSummaries,
    groupByConversation,
    CHANNEL_GROUP_I18N,
    CHANNEL_GROUP_FALLBACK,
} from '../utils/searchChannels';
import { splitByMatches } from '../utils/accentFold';
import { useLanguage } from '../i18n';

const ConversationSidebar = ({
    conversations = [],
    currentConversation,
    pinnedConversationIds = null,
    onSelectConversation,
    onCreateConversation,
    onDeleteConversation,
    onBulkDeleteConversations,
    onUpdateConversation,
    onLoadAllConversations,
    onSearchResultClick,
    onHide,
    loading = false,
    // #432: 'column' = the desktop shell's 300px column; 'sheet' = the same
    // sidebar as a full-screen sheet in the MOBILE shell (opened from the overlay
    // menu), so global search and every conversation action stay reachable on a
    // phone or a portrait tablet.
    variant = 'column',
}) => {
    const { t } = useLanguage();
    const [showMenu, setShowMenu] = useState(null);
    // Conversation Data card target ({id, title}) — null when closed.
    const [dataCardConv, setDataCardConv] = useState(null);
    // Conversation data #434: the card describes ONE conversation at ONE moment.
    // Navigating anywhere (a panel, another chat, a keyboard shortcut — none of
    // which need a pointer press the card's click-away could see) dismisses it.
    const location = useLocation();
    const dataCardRouteRef = useRef(null);
    useEffect(() => {
        const route = location.pathname + location.search;
        if (dataCardRouteRef.current !== null && dataCardRouteRef.current !== route) {
            setDataCardConv(null);
        }
        dataCardRouteRef.current = route;
    }, [location.pathname, location.search]);
    // …and a card about a conversation that no longer exists (deleted here, from
    // the chat header, or by the #433 optimistic removal) must not stay up.
    useEffect(() => {
        if (dataCardConv && !conversations.some((c) => c.id === dataCardConv.id)) {
            setDataCardConv(null);
        }
    }, [conversations, dataCardConv]);
    const [selectedConversations, setSelectedConversations] = useState(new Set());
    const [selectionMode, setSelectionMode] = useState(false);
    const [, setLoadingAll] = useState(false);
    const [renamingId, setRenamingId] = useState(null);
    const [renameTitle, setRenameTitle] = useState('');

    // Global search state
    const [globalSearchQuery, setGlobalSearchQuery] = useState('');
    // Per-conversation summaries with their TRUE occurrence counts (#353), grouped
    // into channel lanes. This is the ONLY search render model — there is no flat
    // list of loose rows any more (see the UI-refinement note on the results JSX).
    const [globalSearchGroups, setGlobalSearchGroups] = useState([]);
    const [totalOccurrences, setTotalOccurrences] = useState(0);
    const [isGlobalSearching, setIsGlobalSearching] = useState(false);
    const searchTimeoutRef = useRef(null);
    // Cancels a superseded in-flight search; `searchRunRef` is the monotonic
    // ticket that makes a late response from an aborted run unable to win.
    const searchAbortRef = useRef(null);
    const searchRunRef = useRef(0);
    const searchInputRef = useRef(null);

    // ── Conversation-group expansion (#353 UI refinement) ────────────────────
    // Groups render COLLAPSED; expanding one fetches that conversation's COMPLETE
    // match list (`preview` mode), which the capped global payload cannot supply.
    const [expandedConvIds, setExpandedConvIds] = useState(() => new Set());
    // convId -> { status: 'loading' | 'ready' | 'error', rows: [] }
    const [convMatches, setConvMatches] = useState({});
    // Per-conversation abort + a monotonic ticket. The ticket map is CLEARED
    // whenever the query changes, which is what keys staleness on (convId, query)
    // without threading the term through every callback: a response for an old
    // term finds no ticket and drops itself.
    const convAbortRef = useRef(new Map());
    const convRunRef = useRef(new Map());

    const draftedIds = useDraftedConversationIds();
    const respondedIds = useRespondedConversationIds();
    // Hide empty, abandoned "New Conversation" entries (Persistence new
    // conversation). A chat surfaces in the list only once it has real content:
    // a title (set the moment the first message is sent) OR a saved draft
    // (orange dot). An untitled chat with neither — e.g. "New Chat" clicked then
    // abandoned — never clutters the list, and legacy empties are cleaned up too.
    const hasContent = (c) => (
        (c.title && c.title !== 'New Conversation') || draftedIds.has(c.id)
        // #290 (New conversations transcription bug): a conversation with an
        // in-flight turn or a queued voice send is PINNED — it renders as
        // "New Conversation" even while untitled and draft-less, so a brand-new
        // voice chat never vanishes during the transcription/dispatch window.
        || (pinnedConversationIds ? pinnedConversationIds.has(c.id) : false)
        // A background response the user hasn't read yet must never be hidden
        // either (belt & braces — the title refresh normally lands first).
        || respondedIds.has(c.id)
    );
    const myConversations = conversations.filter(c => !c.is_shared_with_me && hasContent(c));
    const sharedConversations = conversations.filter(c => c.is_shared_with_me);

    // Debounced global search.
    //
    // #353 — two failure modes the old version had, both of which made a slow
    // search feel much worse than it was:
    //
    //   * NO CANCELLATION. Once the debounce fired, the request ran to completion
    //     even though the user had typed on. With the backend seq-scanning every
    //     message per request, a superseded search kept a server thread busy for
    //     seconds and the next one queued behind it — so latency accumulated
    //     across keystrokes instead of being paid once.
    //   * NO STALE GUARD. Whichever response arrived LAST won, not whichever was
    //     asked for last, so a slow "pa" could overwrite the results for "papa"
    //     and `setIsGlobalSearching(false)` could clear the spinner for a request
    //     that was no longer the current one.
    //
    // The AbortController fixes the first and makes the second impossible for the
    // happy path; `runId` still guards it explicitly, because an abort races the
    // in-flight `await` and a response can land after the signal fires.
    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (searchAbortRef.current) searchAbortRef.current.abort();

        if (!globalSearchQuery.trim()) {
            setGlobalSearchGroups([]);
            setTotalOccurrences(0);
            setIsGlobalSearching(false);
            return;
        }

        const runId = ++searchRunRef.current;
        setIsGlobalSearching(true);
        searchTimeoutRef.current = setTimeout(async () => {
            const controller = new AbortController();
            searchAbortRef.current = controller;
            try {
                const data = await apiService.searchMessagesGlobal(
                    globalSearchQuery.trim(), { signal: controller.signal });
                if (runId !== searchRunRef.current) return;   // superseded
                // ONE render model. A backend that predates `conversations[]` has
                // its flat rows folded into the same per-conversation shape rather
                // than falling back to a second, loose-row layout (#353 UI phase).
                const summaries = data.conversations?.length
                    ? data.conversations
                    : synthesizeConversationSummaries(data.results);
                setGlobalSearchGroups(groupByConversation(summaries, data.results));
                setTotalOccurrences(data.total_occurrences || 0);
            } catch (err) {
                if (err?.name === 'AbortError') return;       // expected, not a failure
                if (runId !== searchRunRef.current) return;
                console.error('Global search error:', err);
                setGlobalSearchGroups([]);
                setTotalOccurrences(0);
            } finally {
                if (runId === searchRunRef.current) setIsGlobalSearching(false);
            }
        }, 300);

        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [globalSearchQuery]);

    // A new term invalidates every cached expansion — the same conversation may
    // still hold hits, but not the same ones — so both the open set and the cached
    // lists are dropped and anything in flight is cancelled. Clearing the ticket
    // map is also what makes a late response for the OLD term unable to land.
    // (Consuming a result clears the query, so this covers that path too.)
    //
    // Both setters are GUARDED on there being something to drop. This effect runs on
    // every keystroke, and an unguarded `setExpandedConvIds(new Set())` /
    // `setConvMatches({})` hands React a fresh object identity each time — two extra
    // re-renders of the whole sidebar per character typed, for no change in state.
    // That is the #206 "jitter" class, so it is spent only when it buys something.
    useEffect(() => {
        setExpandedConvIds((prev) => (prev.size ? new Set() : prev));
        setConvMatches((prev) => (Object.keys(prev).length ? {} : prev));
        for (const controller of convAbortRef.current.values()) controller.abort();
        convAbortRef.current.clear();
        convRunRef.current.clear();
    }, [globalSearchQuery]);

    // Fetch ONE conversation's complete match list. The global payload's `results`
    // is a single capped page across the whole account, so a conversation with 107
    // hits contributes a handful of rows at best — the expansion has to ask the
    // per-conversation route for the rest.
    const fetchConversationMatches = async (convId, query) => {
        const previous = convAbortRef.current.get(convId);
        if (previous) previous.abort();
        const controller = new AbortController();
        convAbortRef.current.set(convId, controller);
        const runId = (convRunRef.current.get(convId) || 0) + 1;
        convRunRef.current.set(convId, runId);
        const isCurrent = () => convRunRef.current.get(convId) === runId;

        setConvMatches((prev) => ({ ...prev, [convId]: { status: 'loading', rows: [] } }));
        try {
            const data = await apiService.searchMessagesInConversation(
                convId, query, { preview: true, signal: controller.signal });
            if (!isCurrent()) return;
            setConvMatches((prev) => ({
                ...prev, [convId]: { status: 'ready', rows: data.results || [] },
            }));
        } catch (err) {
            if (err?.name === 'AbortError') return;           // expected, not a failure
            if (!isCurrent()) return;
            console.error('Conversation match fetch error:', err);
            setConvMatches((prev) => ({ ...prev, [convId]: { status: 'error', rows: [] } }));
        }
    };

    const toggleConversationExpansion = (e, conv) => {
        e.stopPropagation();
        const convId = conv.conversation_id;
        const query = globalSearchQuery.trim();
        const wasExpanded = expandedConvIds.has(convId);
        setExpandedConvIds((prev) => {
            const next = new Set(prev);
            if (next.has(convId)) next.delete(convId);
            else next.add(convId);
            return next;
        });
        // Fetch on the way OPEN only, and only when we don't already hold the list
        // — re-collapsing and re-opening must not re-scan.
        if (!wasExpanded && query && convMatches[convId]?.status !== 'ready') {
            fetchConversationMatches(convId, query);
        }
    };

    // A row inside an expanded group. Built from the PARENT conversation plus the
    // slim row, so the two sources (the global payload's nav rows and the fetched
    // preview rows) produce an identical click payload. The channel fields are
    // load-bearing: they are what deep-links a phone bucket into its own panel
    // instead of dragging it into the Main Chat (see `channelDeepLink`).
    const buildMessageRow = (conv, row) => ({
        result_type: 'content',
        conversation_id: conv.conversation_id,
        conversation_title: conv.conversation_title,
        channel: conv.channel,
        channel_group: conv.channel_group,
        world: conv.world,
        day_date: conv.day_date,
        message_id: row.message_id,
        message_preview: row.message_preview,
        message_type: row.message_type,
        created_at: row.created_at,
    });

    const handleSearchResultClick = (result) => {
        if (onSearchResultClick) {
            // The whole row travels: the caller needs `channel_group`/`world` to
            // deep-link a phone bucket into its own panel instead of dragging it
            // into the Main Chat. (The first three args are unchanged.)
            onSearchResultClick(result.conversation_id, result.message_id, globalSearchQuery.trim(), result);
        }
        // Clearing the query also resets the expansion state — see the effect above.
        setGlobalSearchQuery('');
        setGlobalSearchGroups([]);
        setTotalOccurrences(0);
    };

    // Two bugs fixed here (#353): the old version built its regex from the RAW
    // query, so an accent-insensitive backend hit ("papá" found by "papa") came
    // back unhighlighted; and it decided which part was a match with
    // `regex.test(part)` on a /g/ regex — whose `lastIndex` is stateful, so
    // alternating parts silently failed the test. Using the shared fold pattern and
    // index parity makes both impossible.
    const highlightPreview = (preview, query) => {
        const parts = splitByMatches(preview, query);
        if (!parts) return preview;
        return parts.map((part, i) =>
            i % 2 === 1
                ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-700 text-inherit rounded-sm px-0.5">{part}</mark>
                : part
        );
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (diffInHours < 24 * 7) {
            return date.toLocaleDateString([], { weekday: 'short' });
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }
    };

    const handleNewChat = () => {
        setDataCardConv(null);
        onCreateConversation();
    };

    const handleDeleteConversation = (e, conversationId) => {
        e.stopPropagation();
        setDataCardConv(null);   // #434: a new action never runs under the data card
        onDeleteConversation(conversationId);
        setShowMenu(null);
    };

    // Export the full conversation debug trace (every turn's message + metadata
    // + the raw LLM calls) as a downloadable JSON — used to ship a conversation
    // off for root-cause debugging of the thinking process.
    const handleExportConversation = async (e, conversationId) => {
        e.stopPropagation();
        setShowMenu(null);
        setDataCardConv(null);
        try {
            const { blob, filename } = await apiService.exportConversation(conversationId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export conversation:', err);
            alert(t('navExportFailed', 'Could not export this conversation. Please try again.'));
        }
    };


    const handleStartRename = (e, conversationId, currentTitle) => {
        e.stopPropagation();
        setDataCardConv(null);
        setRenamingId(conversationId);
        setRenameTitle(currentTitle || t('navNewConversationTitle', 'New Conversation'));
        setShowMenu(null);
    };

    const handleCancelRename = () => {
        setRenamingId(null);
        setRenameTitle('');
    };

    const handleSaveRename = async (e, conversationId) => {
        e.stopPropagation();
        if (!renameTitle.trim()) {
            handleCancelRename();
            return;
        }

        try {
            await onUpdateConversation(conversationId, { title: renameTitle.trim() });
            setRenamingId(null);
            setRenameTitle('');
        } catch (error) {
            console.error('Error renaming conversation:', error);
            alert(t('navRenameFailed', 'Failed to rename conversation'));
        }
    };

    const handleRenameKeyPress = (e, conversationId) => {
        if (e.key === 'Enter') {
            handleSaveRename(e, conversationId);
        } else if (e.key === 'Escape') {
            handleCancelRename();
        }
    };

    const toggleMenu = (e, conversationId) => {
        e.stopPropagation();
        setDataCardConv(null);
        setShowMenu(showMenu === conversationId ? null : conversationId);
    };

    // Auto-close the conversation menu on ANY click outside it (Conversation Data
    // ticket — the menu should no longer need a second click on the dots).
    useEffect(() => {
        if (!showMenu) return undefined;
        const onDocClick = (e) => {
            if (!e.target.closest('[data-conv-menu]')) setShowMenu(null);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [showMenu]);

    const handleOpenData = (e, conversation) => {
        e.stopPropagation();
        setDataCardConv({ id: conversation.id, title: conversation.title });
        setShowMenu(null);
    };

    const toggleSelectionMode = async () => {
        const newMode = !selectionMode;

        if (newMode && onLoadAllConversations) {
            setLoadingAll(true);
            try {
                await onLoadAllConversations();
            } catch (error) {
                console.error('Error loading all conversations:', error);
            } finally {
                setLoadingAll(false);
            }
        }

        setSelectionMode(newMode);
        setSelectedConversations(new Set());
    };

    // Enter multi-select mode from a conversation's kebab menu (Sidebar
    // restructure #186). The clicked conversation is pre-checked so the user
    // sees the bulk "Eliminate" control immediately, then can check more.
    const handleStartSelectionFromMenu = async (e, conversation) => {
        e.stopPropagation();
        setShowMenu(null);
        setDataCardConv(null);

        if (onLoadAllConversations) {
            setLoadingAll(true);
            try {
                await onLoadAllConversations();
            } catch (error) {
                console.error('Error loading all conversations:', error);
            } finally {
                setLoadingAll(false);
            }
        }

        setSelectionMode(true);
        setSelectedConversations(new Set([conversation.id]));
    };

    const toggleConversationSelection = (e, conversationId) => {
        e.stopPropagation();
        const newSelected = new Set(selectedConversations);
        if (newSelected.has(conversationId)) {
            newSelected.delete(conversationId);
        } else {
            newSelected.add(conversationId);
        }
        setSelectedConversations(newSelected);
    };

    const toggleSelectAll = () => {
        if (selectedConversations.size === myConversations.length) {
            setSelectedConversations(new Set());
        } else {
            setSelectedConversations(new Set(myConversations.map(c => c.id)));
        }
    };

    const handleDeleteSelected = async () => {
        if (selectedConversations.size === 0) return;

        const confirmMsg = t(
            'navDeleteConversationsConfirm',
            'Delete {n} conversation{s}?',
            { n: selectedConversations.size, s: selectedConversations.size > 1 ? 's' : '' }
        );
        if (!window.confirm(confirmMsg)) return;

        // #433: leave selection mode NOW — the rows leave the list optimistically
        // (useConversations), so there is nothing left to wait on. A failure
        // restores the rows and is reported by the host (MainOfficeView's toast).
        const ids = Array.from(selectedConversations);
        setSelectedConversations(new Set());
        setSelectionMode(false);

        if (onBulkDeleteConversations) {
            try {
                await onBulkDeleteConversations(ids);
            } catch (error) {
                console.error('Error bulk deleting conversations:', error);
            }
            return;
        }
        try {
            for (const conversationId of ids) {
                try {
                    await onDeleteConversation(conversationId);
                } catch (error) {
                    console.error(`Error deleting conversation ${conversationId}:`, error);
                }
            }
        } catch (error) {
            console.error('Error bulk deleting conversations:', error);
            alert(t('navBulkDeleteFailed', 'Failed to delete some conversations. Please try again.'));
        }
    };

    const renderConversationItem = (conversation, isShared = false) => {
        const isSelected = selectedConversations.has(conversation.id);
        return (
            <div
                key={conversation.id}
                data-testid="conversation-item"
                data-conv-id={conversation.id}
                /* "You are here" as a STRUCTURAL fact, not a colour. The row's
                   highlight has changed shape twice now (blue ring → the PAI
                   glow), and a spec that reads the class breaks every time the
                   skin does. */
                data-current={currentConversation?.id === conversation.id && !selectionMode ? 'true' : 'false'}
                className={`relative group flex items-center gap-3 px-3.5 py-3 rounded-row cursor-pointer transition-colors border-[1.5px] ${
                    currentConversation?.id === conversation.id && !selectionMode
                        ? 'border-white/80 bg-white/[0.055] shadow-glow-sm'
                        : isSelected && selectionMode
                        ? 'border-white/40 bg-white/[0.05]'
                        : 'border-transparent hover:bg-white/[0.05]'
                }`}
                onClick={(e) => {
                    if (renamingId === conversation.id) return;
                    if (selectionMode && !isShared) {
                        toggleConversationSelection(e, conversation.id);
                    } else {
                        setDataCardConv(null);   // #434
                        onSelectConversation(conversation);
                    }
                }}
            >
                {/* Leading indicator: the checkbox (select mode) and the Users icon
                    (shared conversations) are meaningful state markers and stay. The
                    per-row "speech bubble" (MessageCircle) icon that used to sit before
                    every OWNED conversation title was purely decorative and is removed
                    (Conversation Icon #239) — an owned row now shows only its title,
                    with alignment handled by the row's flex gap. */}
                {selectionMode && !isShared ? (
                    <div className="flex-shrink-0">
                        {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-blue-500" />
                        ) : (
                            <Square className="w-5 h-5 text-white/45" />
                        )}
                    </div>
                ) : isShared ? (
                    <Users className="w-4 h-4 text-blue-400 flex-shrink-0" />
                ) : null}

                <div className="flex-1 min-w-0">
                    {!isShared && renamingId === conversation.id ? (
                        <input
                            type="text"
                            value={renameTitle}
                            onChange={(e) => setRenameTitle(e.target.value)}
                            onKeyDown={(e) => handleRenameKeyPress(e, conversation.id)}
                            onBlur={(e) => handleSaveRename(e, conversation.id)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            className="w-full text-sm font-medium text-white bg-white/[0.06] border border-white/80 rounded-lg px-2 py-1 focus:outline-none shadow-glow-sm"
                        />
                    ) : (
                        <>
                            <div className="flex items-center gap-1.5">
                                <div className="text-sm font-medium text-white truncate flex-1 min-w-0">
                                    {conversation.title || t('navNewConversationTitle', 'New Conversation')}
                                </div>
                                {!isShared && draftedIds.has(conversation.id) && (
                                    <span
                                        className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-draft-pulse flex-shrink-0"
                                        title={t('navUnfinishedDraft', 'Unfinished draft')}
                                    />
                                )}
                                {/* Fire-and-Forget: solid, non-blinking green dot for a
                                    background response waiting to be read. */}
                                {!isShared && respondedIds.has(conversation.id) && (
                                    <span
                                        className="inline-block w-2 h-2 rounded-full bg-green-500 flex-shrink-0"
                                        title={t('navNewResponseReady', 'New response ready')}
                                    />
                                )}
                            </div>
                            {/* Sidebar Cleanup: no timestamp/message-count subtext —
                                the entry shows only the conversation name. Shared
                                conversations keep the "from <user>" attribution. */}
                            {isShared && conversation.shared_by_username && (
                                <div className="text-xs text-blue-500 dark:text-blue-400 mt-1">
                                    {t('navFromUser', 'from {name}', { name: conversation.shared_by_username })}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Menu Button - Only show for owned conversations, not in selection mode */}
                {!isShared && !selectionMode && renamingId !== conversation.id && (
                    <>
                        <button
                            data-conv-menu
                            onClick={(e) => toggleMenu(e, conversation.id)}
                            title={t('navConversationOptions', 'Conversation options')}
                            className="opacity-0 group-hover:opacity-100 pai-touch-reveal p-1 rounded hover:bg-white/[0.07] transition-opacity"
                        >
                            <MoreVertical className="w-4 h-4 text-pai-meta" />
                        </button>

                        {showMenu === conversation.id && (
                            <div data-conv-menu className="absolute right-2 top-10 z-10 w-48 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1">
                                <button
                                    data-testid="conv-menu-data"
                                    onClick={(e) => handleOpenData(e, conversation)}
                                    className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/[0.07] flex items-center gap-2"
                                >
                                    <BarChart3 className="w-4 h-4" />
                                    {t('navConversationData', 'Conversation Data')}
                                </button>
                                <button
                                    data-testid="conv-menu-rename"
                                    onClick={(e) => handleStartRename(e, conversation.id, conversation.title)}
                                    className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/[0.07] flex items-center gap-2"
                                >
                                    <Edit2 className="w-4 h-4" />
                                    {t('commonRename', 'Rename')}
                                </button>
                                {/* Select — enters multi-select mode (Sidebar restructure
                                    #186). Placed between Rename and Export per the ticket. */}
                                <button
                                    data-testid="conv-menu-select"
                                    onClick={(e) => handleStartSelectionFromMenu(e, conversation)}
                                    className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/[0.07] flex items-center gap-2"
                                >
                                    <CheckSquare className="w-4 h-4" />
                                    {t('select', 'Select')}
                                </button>
                                <button
                                    data-testid="conv-menu-export"
                                    onClick={(e) => handleExportConversation(e, conversation.id)}
                                    title={t('navExportTraceTitle', 'Download a full debug trace (messages, thinking steps, prompts, LLM calls)')}
                                    className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/[0.07] flex items-center gap-2"
                                >
                                    <Download className="w-4 h-4" />
                                    {t('navExport', 'Export')}
                                </button>
                                <button
                                    data-testid="conv-menu-delete"
                                    onClick={(e) => handleDeleteConversation(e, conversation.id)}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    {t('commonDelete', 'Delete')}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    const showSearchResults = globalSearchQuery.trim().length > 0;

    return (
        // Hidden on phone widths (Mobile Visual #171) — the app-like overlay menu
        // owns conversation switching there; restored as a column at md+.
        <div
            data-testid="conversation-sidebar"
            data-variant={variant}
            className={variant === 'sheet'
                ? 'flex w-full flex-1 min-w-0 overflow-hidden flex-col h-full'
                : 'hidden shell:flex w-[300px] flex-none pai-panel overflow-hidden flex-col h-full'}
        >
            {dataCardConv && (
                <ConversationDataCard
                    conversationId={dataCardConv.id}
                    title={dataCardConv.title}
                    onClose={() => setDataCardConv(null)}
                />
            )}
            {/* Header (Sidebar restructure #186): the Search bar sits at the very
                top with "New Chat" directly underneath it, in BOTH modes. The old
                standalone header "Select" button is gone — multi-select is now
                entered from a conversation's kebab menu. In selection mode the bulk
                "Eliminate" control appears just under the Search + New Chat area. */}
            <div className="p-4 border-b border-pai-hairline space-y-2">
                {/* Global Search Bar — always at the top, with the column's own
                    collapse arrow beside it (#360). The control belongs to the thing
                    it hides: the old three-lines button lived in the CHAT header,
                    where nothing said which surface it was about. */}
                <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/45" />
                    <input
                        ref={searchInputRef}
                        data-testid="sidebar-search"
                        type="text"
                        value={globalSearchQuery}
                        onChange={(e) => setGlobalSearchQuery(e.target.value)}
                        placeholder={t('navSearchAllChats', 'Search all chats...')}
                        className="w-full pl-8 pr-8 py-1.5 text-sm pai-input focus:outline-none"
                    />
                    {globalSearchQuery && (
                        <button
                            onClick={() => setGlobalSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/45 hover:text-pai-secondary "
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
                {onHide && (
                    <button
                        type="button"
                        data-testid="conversation-sidebar-hide"
                        onClick={onHide}
                        title={t('navHideConversations', 'Hide conversations')}
                        aria-label={t('navHideConversations', 'Hide conversations')}
                        className="pai-icon-btn w-[30px] h-[30px] flex-none"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                )}
                </div>

                {/* New Chat — directly underneath the Search bar */}
                <button
                    data-testid="sidebar-new-chat"
                    onClick={handleNewChat}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm font-medium text-white border border-pai bg-white/[0.04] rounded-ctl hover:bg-white/[0.09] hover:border-pai-strong transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    {t('newChat')}
                </button>

                {/* Multi-select controls — only while selection mode is active */}
                {selectionMode && (
                    <>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={toggleSelectAll}
                                className="flex-1 flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border border-pai bg-white/[0.04] rounded-ctl hover:bg-white/[0.09] hover:border-pai-strong transition-colors"
                            >
                                {selectedConversations.size === myConversations.length ? (
                                    <CheckSquare className="w-4 h-4 text-blue-500" />
                                ) : (
                                    <Square className="w-4 h-4" />
                                )}
                                {t('navAllCount', 'All ({n})', { n: myConversations.length })}
                            </button>
                            <button
                                onClick={toggleSelectionMode}
                                className="px-4 py-2 text-sm font-medium text-white border border-pai bg-white/[0.04] rounded-ctl hover:bg-white/[0.09] hover:border-pai-strong transition-colors"
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                        </div>
                        {selectedConversations.size > 0 && (
                            <button
                                data-testid="sidebar-eliminate"
                                onClick={handleDeleteSelected}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                            >
                                <Trash2 className="w-4 h-4" />
                                {t('navEliminateCount', 'Eliminate ({n})', { n: selectedConversations.size })}
                            </button>
                        )}
                    </>
                )}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
                {showSearchResults ? (
                    /* Global Search Results */
                    <div className="p-2">
                        {isGlobalSearching ? (
                            <div className="p-4 text-center text-pai-meta text-sm">
                                {t('navSearching', 'Searching...')}
                            </div>
                        ) : globalSearchGroups.length === 0 ? (
                            <div className="p-4 text-center text-pai-meta text-sm">
                                {t('navNoMessagesFound', 'No messages found')}
                            </div>
                        ) : (
                            /* ONE render model (#353 UI refinement). EVERY result is
                               nested under its parent conversation — there is no path
                               that can put a loose message row in this list any more.
                               The old code had two: a grouped one whose groups were
                               permanently expanded (so all their rows spilled into one
                               long list), split into a "Conversations" section of
                               title-matched chats above a "Messages" section — which is
                               why a chat matched by NAME rendered as a bare row with no
                               count and no way to reach its messages, while its
                               occurrences were still counted in the lane total.

                               The CHANNEL lanes are deliberately KEPT: a Telegram day
                               bucket and a Main Chat thread open different surfaces, so
                               the lane is what tells the user where a hit lives.

                               #185 survives as ORDERING, not as a section split — the BE
                               already returns `conversations[]` title-matches-first
                               (exact → prefix → substring) — and is made assertable by
                               `data-title-match` on each group. */
                            <div className="space-y-3">
                                <div
                                    data-testid="search-total-occurrences"
                                    data-total={totalOccurrences}
                                    className="px-2 pb-1 text-[11px] text-pai-meta"
                                >
                                    {t('navSearchTotalOccurrences', '{n} results', { n: totalOccurrences })}
                                </div>
                                {globalSearchGroups.map((group) => (
                                    <div
                                        key={group.key}
                                        data-testid="search-channel-group"
                                        data-channel={group.key}
                                        data-total={group.total}
                                        className="space-y-1"
                                    >
                                        <div
                                            data-testid="search-channel-header"
                                            data-channel={group.key}
                                            data-total={group.total}
                                            className="px-2 pt-1 text-xs font-bold text-white tracking-wide border-b border-pai-hairline pb-1"
                                        >
                                            {t(CHANNEL_GROUP_I18N[group.key], CHANNEL_GROUP_FALLBACK[group.key])} ({group.total})
                                        </div>

                                        {group.conversations.map((conv) => {
                                            const convId = conv.conversation_id;
                                            const expanded = expandedConvIds.has(convId);
                                            const entry = convMatches[convId];
                                            const loaded = entry?.status === 'ready';
                                            // Until the complete list lands, show the rows
                                            // the global payload already carried — they
                                            // are real matches, just an incomplete subset,
                                            // so expanding never flashes empty.
                                            const rows = loaded ? entry.rows : (conv.rows || []);
                                            const expandLabel = expanded
                                                ? t('navSearchCollapseMessages', 'Hide matching messages')
                                                : t('navSearchExpandMessages', 'Show matching messages');
                                            return (
                                                <div
                                                    key={convId}
                                                    data-testid="search-conversation-group"
                                                    data-conv-id={convId}
                                                    data-channel={group.key}
                                                    data-occurrences={conv.occurrences}
                                                    data-title-match={conv.title_match ? 'true' : 'false'}
                                                    data-expanded={expanded ? 'true' : 'false'}
                                                    /* `group` is what the hover-reveal CSS
                                                       hangs off — see .pai-reveal-on-hover. */
                                                    className="group space-y-1 min-w-0"
                                                >
                                                    {/* The header holds two controls and no
                                                        click handler of its own: the NAME
                                                        opens the chat, the arrow expands.
                                                        The title button spans the row up to
                                                        the arrow, so a click anywhere but
                                                        the arrow still opens. */}
                                                    <div
                                                        data-testid="search-conversation-header"
                                                        data-conv-id={convId}
                                                        data-occurrences={conv.occurrences}
                                                        className="flex items-center rounded-lg hover:bg-white/[0.07] transition-colors min-w-0"
                                                    >
                                                        <button
                                                            type="button"
                                                            data-testid="search-conversation-title"
                                                            onClick={() => handleSearchResultClick({ ...conv, message_id: null, result_type: 'title' })}
                                                            title={conv.conversation_title}
                                                            className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 text-left"
                                                        >
                                                            <MessageCircle className="w-3.5 h-3.5 text-white/45 flex-shrink-0" />
                                                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400 truncate flex-1 min-w-0">
                                                                {highlightPreview(conv.conversation_title, globalSearchQuery)}
                                                            </span>
                                                            <span
                                                                data-testid="search-conversation-count"
                                                                className="text-[10px] font-semibold text-pai-meta flex-shrink-0"
                                                            >
                                                                {conv.occurrences > 0
                                                                    ? t('navSearchOccurrences', '{n} occurrences', { n: conv.occurrences })
                                                                    : t('navSearchNameMatch', 'Name match')}
                                                            </span>
                                                        </button>
                                                        {/* A title-only match has no messages
                                                            to expand, so it gets no arrow. */}
                                                        {conv.occurrences > 0 && (
                                                            <button
                                                                type="button"
                                                                data-testid="search-conversation-expand"
                                                                aria-expanded={expanded}
                                                                aria-label={expandLabel}
                                                                title={expandLabel}
                                                                onClick={(e) => toggleConversationExpansion(e, conv)}
                                                                className="pai-reveal-on-hover flex-none mr-1 p-1 rounded text-white/70 hover:text-white"
                                                            >
                                                                <ChevronDown
                                                                    className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                                                                />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {expanded && (
                                                        /* No inner scroller: the sidebar
                                                           already scrolls, and a nested
                                                           scrollport inside a 300px column
                                                           is hostile to reach. */
                                                        <div
                                                            data-testid="search-conversation-messages"
                                                            data-loaded={loaded ? 'true' : 'false'}
                                                            className="ml-4 space-y-1 min-w-0"
                                                        >
                                                            {rows.map((row, idx) => (
                                                                <button
                                                                    key={`${row.message_id || 'row'}-${idx}`}
                                                                    type="button"
                                                                    data-testid="search-content-result"
                                                                    data-conv-id={convId}
                                                                    data-message-id={row.message_id}
                                                                    data-channel={group.key}
                                                                    className="w-full min-w-0 text-left p-2 rounded-lg hover:bg-white/[0.07] transition-colors"
                                                                    onClick={() => handleSearchResultClick(buildMessageRow(conv, row))}
                                                                >
                                                                    <div className="text-xs text-pai-secondary line-clamp-2 leading-relaxed break-words">
                                                                        {highlightPreview(row.message_preview, globalSearchQuery)}
                                                                    </div>
                                                                    <div className="text-xs text-pai-faint mt-1">
                                                                        {row.message_type === 'user' ? t('navYou', 'You') : t('navAi', 'AI')} · {formatDate(row.created_at)}
                                                                    </div>
                                                                </button>
                                                            ))}
                                                            {entry?.status === 'loading' && (
                                                                <div
                                                                    data-testid="search-conversation-loading"
                                                                    className="px-2 py-1.5 text-[11px] text-pai-meta"
                                                                >
                                                                    {t('navSearchLoadingMessages', 'Loading matches...')}
                                                                </div>
                                                            )}
                                                            {entry?.status === 'error' && (
                                                                <button
                                                                    type="button"
                                                                    data-testid="search-conversation-error"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        fetchConversationMatches(convId, globalSearchQuery.trim());
                                                                    }}
                                                                    className="w-full text-left px-2 py-1.5 text-[11px] text-pai-red hover:underline"
                                                                >
                                                                    {t('navSearchMessagesError', 'Could not load matches')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (loading && conversations.length === 0) ? (
                    /* #206 (re-open): the "Loading conversations..." placeholder only
                       ever renders on the INITIAL cold load (no rows yet). Once the
                       list has data it is NEVER replaced by the placeholder —
                       stale-while-revalidate — so a background refetch can't blank the
                       sidebar. (Belt 2: the ghost fetch also stops toggling `loading`
                       at all — see useConversations.) */
                    <div className="p-4 text-center text-pai-meta">
                        {t('navLoadingConversations', 'Loading conversations...')}
                    </div>
                ) : conversations.length === 0 ? (
                    <div className="p-4 text-center text-pai-meta">
                        <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{t('noConversations', 'No conversations yet')}</p>
                        <p className="text-xs mt-1">{t('navStartNewChat', 'Start a new chat to begin')}</p>
                    </div>
                ) : (
                    <>
                        {/* My Conversations */}
                        <div className="p-2 space-y-1">
                            {myConversations.map((conversation) => renderConversationItem(conversation, false))}
                        </div>

                        {/* Shared with me */}
                        {sharedConversations.length > 0 && (
                            <div className="border-t border-pai-hairline">
                                <div className="px-4 py-2 text-xs font-semibold text-pai-meta uppercase tracking-wider">
                                    {t('navSharedWithMe', 'Shared with me')}
                                </div>
                                <div className="px-2 pb-2 space-y-1">
                                    {sharedConversations.map((conversation) => renderConversationItem(conversation, true))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-pai-hairline">
                <div className="text-xs text-pai-meta text-center">
                    PAI
                </div>
            </div>
        </div>
    );
};

export default ConversationSidebar;
